import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { NodeStatus } from '@dbt-labs/dbt-dag';
import {
  ChartTable,
  CostChart,
  CostInsightsConnectionBadge,
  downloadCostInsightsCsv,
  downloadUsageCsv,
  groupDataByTimeGrouping,
  Grouping,
  isInitialCostProcessing,
  OverviewContainer,
  PENDING_TOOLTIP_CONTENT,
  ResourceNodeType,
  RunChart,
  SUCCESS_TOOLTIP_CONTENT,
  UsageChart,
  UsageChartDataPoint,
  useCostInsights,
  useCostInsightsBanner,
  useCostInsightsConnectionTestStatus,
  useCostInsightsEnablement,
} from '@dbt-labs/metadata-shared';
import {
  Button,
  Card,
  FloatingTabs,
  MenuButton,
  RyeconCaretDown,
  RyeconCaretUp,
  RyeconDownload,
  Table,
} from '@dbt-labs/sourdough';

import { JobInformation } from '@/__generated__/graphql';
import { AverageModelExecutionChart } from '@/components/Charts/AverageModelExecutionChart';
import {
  ChartDateDropdown,
  DateRange,
  DEFAULT_DATE_RANGE,
} from '@/components/Charts/ChartDateDropdown';
import { ExecutionRunTable } from '@/components/Charts/ExecutionRunTable';
import { ModelExecutionStatusStackedBarChart } from '@/components/Charts/ModelExecutionStatusStackedBarChart';
import { ModelQueryUsageBarChart } from '@/components/Charts/ModelQueryUsageBarChart';
import { ModelTestExecutionStatusStackedBarChart } from '@/components/Charts/ModelTestExecutionStatusStackedBarChart';
import { DetailsSection } from '@/components/SectionWithCard';
import { LinkCell } from '@/components/Table/LinkCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useFeatureFlags } from '@/context/FeatureFlagProvider';
import { useProject } from '@/hooks/admin/project';
import { useJobInformation } from '@/hooks/discovery/jobInformation';
import { useLinkToDbtCloud } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { formatDateUtcYYYYMMDD } from '@/util/date';
import { ExecutionInfo, RecentSection } from '../Sections/RecentSection';

interface PerformanceViewProps {
  uniqueId: string;
  executionInfo: ExecutionInfo | null | undefined;
  resourceType?: 'model' | 'test' | 'snapshot';
}

const tabTypes = [
  'cost insights',
  'usage',
  'query run time',
  'build time',
  'build count',
  'test results',
  'consumption queries',
] as const;
type TabType = (typeof tabTypes)[number];

export type JobFilter = {
  date: string;
  status?: NodeStatus;
};

type JobTableDate = {
  jobName: string;
  jobId: number;
};

const getJobTableData = (jobs: JobInformation[]): JobTableDate[] => {
  return jobs.map((job) => {
    return {
      jobName: job.name || '',
      jobId: job.jobId || 0,
    };
  });
};

export const PerformanceView: FC<PerformanceViewProps> = ({
  uniqueId,
  executionInfo,
  resourceType = 'model',
}) => {
  const isTestResource = resourceType === 'test';
  const isSnapshotResource = resourceType === 'snapshot';
  const {
    accountId,
    projectId,
    environmentId,
    enableModelQueryHistory: showUsageTab,
  } = useDbtCloudProject();

  const {
    meta5835EnableCostEstimationFrontend,
    orc2609ShowFusionToggle,
    orc2664SaoBeta,
  } = useFeatureFlags();

  const featureFlags = useMemo(
    () => ({
      meta5835EnableCostEstimationFrontend: !!meta5835EnableCostEstimationFrontend,
      orc2609ShowFusionToggle: !!orc2609ShowFusionToggle,
      orc2664SaoBeta: !!orc2664SaoBeta,
    }),
    [meta5835EnableCostEstimationFrontend, orc2609ShowFusionToggle, orc2664SaoBeta],
  );

  const {
    canViewProjectCostData,
    canViewProjectNonCostData,
    isLoading: isCostInsightsEnablementLoading,
  } = useCostInsightsEnablement({
    accountId,
    environmentId,
    projectId,
    featureFlags,
  });

  const banner = useCostInsightsBanner({
    accountId,
    environmentId,
    projectId,
    featureFlags,
    jobId: executionInfo?.lastJobDefinitionId ?? undefined,
  });

  const projectScope = useUrlProjectParams();
  const getJobLink = useLinkToDbtCloud('jobDetails');
  const getConnectionDetailsLink = useLinkToDbtCloud('connectionDetails');

  const { data: project } = useProject({ accountId, projectId });
  const connectionSettingsUrl =
    getConnectionDetailsLink && accountId && project?.connection_id
      ? getConnectionDetailsLink({ accountId, connectionId: project.connection_id })
      : undefined;

  const { data: connectionTestStatus } = useCostInsightsConnectionTestStatus(
    accountId,
    canViewProjectCostData ? (project?.connection_id ?? undefined) : undefined,
  );

  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  // Always start with 'build time' for models - useEffect will update to 'cost insights' when ready
  // This prevents race conditions with async hooks that determine cost insights access.
  // For test and snapshot resources, 'build time' is filtered out, so start with 'cost insights' instead.
  const [active, setActive] = useState<TabType>(
    isTestResource || isSnapshotResource ? 'cost insights' : 'build time',
  );
  const [selectedDayFilter, setSelectedDayFilter] = useState<JobFilter | null>(null);
  const [buildTimeFilterDate, setBuildTimeFilterDate] = useState<string | null>(null);
  const oneMonthAgoDate = useMemo(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date;
  }, []);
  const currentDate = useMemo(() => new Date(), []);
  const [oldestAvailableDate, setOldestAvailableDate] = useState<Date | undefined>(
    undefined,
  );
  const [jobInformationQuery] = useJobInformation({
    startDate: formatDateUtcYYYYMMDD(oneMonthAgoDate),
    endDate: formatDateUtcYYYYMMDD(currentDate),
    uniqueId,
    environmentId: environmentId || 0,
    pause: isTestResource || isSnapshotResource,
  });
  const jobInformation = jobInformationQuery.data?.performance?.jobInformation;
  const pendingData = useMemo(() => {
    if (!jobInformation) return undefined;
    return getJobTableData(jobInformation);
  }, [jobInformation]);
  const isInitialLoad = jobInformation === undefined;

  // Get cost insights data for overview cards and charts
  const { data: costInsightsData = [], isLoading: costInsightsLoading } =
    useCostInsights({
      environmentId: environmentId || 0,
      uniqueId,
      resourceTypes: isTestResource
        ? [ResourceNodeType.Test]
        : isSnapshotResource
          ? [ResourceNodeType.Snapshot]
          : null,
      pause: !canViewProjectNonCostData,
    });

  // Detect initial cost processing state using unfiltered data
  // so date range filters don't produce false negatives
  const isCostPreparing = useMemo(
    () => isInitialCostProcessing(costInsightsData),
    [costInsightsData],
  );

  const statusBadge = useMemo(() => {
    if (!connectionTestStatus) return undefined;
    // For succeeded status, wait for cost data to load to show correct badge variant
    if (connectionTestStatus.status === 'succeeded' && costInsightsLoading)
      return undefined;
    return (
      <CostInsightsConnectionBadge
        statusOverview={connectionTestStatus}
        connectionSettingsUrl={connectionSettingsUrl}
        isCostProcessed={!isCostPreparing}
        tooltipContent={
          isCostPreparing ? PENDING_TOOLTIP_CONTENT : SUCCESS_TOOLTIP_CONTENT
        }
      />
    );
  }, [
    connectionTestStatus,
    connectionSettingsUrl,
    costInsightsLoading,
    isCostPreparing,
  ]);

  // Track if we've already set the tab based on cost insights enablement to prevent unnecessary updates
  const hasSetTabFromCostInsights = useRef(false);

  // Compute available tabs based on metric visibility:
  // 'full'    → cost insights + usage + query run time
  // 'partial' → usage + query run time (no cost insights)
  // 'none'    → none of the cost-related tabs
  const availableTabs = useMemo(() => {
    return tabTypes.filter(
      (t) =>
        (t !== 'consumption queries' ||
          (showUsageTab && !isTestResource && !isSnapshotResource)) &&
        (t !== 'test results' || (!isTestResource && !isSnapshotResource)) &&
        (t !== 'build time' || (!isTestResource && !isSnapshotResource)) &&
        (t !== 'build count' || (!isTestResource && !isSnapshotResource)) &&
        (t !== 'cost insights' || canViewProjectCostData) &&
        (t !== 'usage' || canViewProjectNonCostData) &&
        (t !== 'query run time' || canViewProjectNonCostData),
    );
  }, [
    showUsageTab,
    canViewProjectCostData,
    canViewProjectNonCostData,
    isTestResource,
    isSnapshotResource,
  ]);

  // Ensure active tab is always valid - if current active tab is filtered out, reset to first available tab
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(active)) {
      setActive(availableTabs[0]);
    }
  }, [active, availableTabs]);

  // Permissions are async, so we need the useEffect to set the active tab after the permission is checked
  // Only update once when cost insights becomes accessible and the tab is available
  useEffect(() => {
    if (
      canViewProjectCostData &&
      !hasSetTabFromCostInsights.current &&
      availableTabs.includes('cost insights')
    ) {
      setActive('cost insights');
      hasSetTabFromCostInsights.current = true;
    }
  }, [canViewProjectCostData, availableTabs]);

  // Reset filters when date range changes
  useEffect(() => {
    setBuildTimeFilterDate(null);
    setSelectedDayFilter(null);
  }, [dateRange.startDate, dateRange.endDate]);

  // Table state for cost insights and usage charts
  const [grouping, setGrouping] = useState<Grouping>('daily');
  const [showChartTable, setShowChartTable] = useState(true);

  const filteredCostData = useMemo(() => {
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);

    return costInsightsData.filter((item) => {
      return item.date >= start && item.date <= end;
    });
  }, [costInsightsData, dateRange.startDate, dateRange.endDate]);

  const groupedCostData = useMemo(() => {
    return groupDataByTimeGrouping(filteredCostData, grouping);
  }, [filteredCostData, grouping]);

  // Transform cost insights data for UsageChart
  const usageChartData: UsageChartDataPoint[] = useMemo(() => {
    return groupedCostData.map((item) => ({
      date: item.date.toISOString().split('T')[0],
      creditsUsed: item.executionComputeUnits,
      creditsAvoided: item.executionComputeUnitsSaved,
    }));
  }, [groupedCostData]);

  // Download handlers
  const handleCostInsightsDownload = useCallback(() => {
    downloadCostInsightsCsv(groupedCostData, grouping);
  }, [groupedCostData, grouping]);

  const handleUsageDownload = useCallback(() => {
    downloadUsageCsv(
      groupedCostData.map((item) => ({
        date: item.date.toISOString().split('T')[0],
        builds: item.executionCount,
        reused: item.reusedCount,
        runDuration: item.executionTime,
        creditsUsed: item.executionComputeUnits,
        creditsSaved: item.executionComputeUnitsSaved,
        cost: item.executionCost,
        costSavings: item.executionCostSaved,
      })),
      grouping,
    );
  }, [groupedCostData, grouping]);

  // Determine if table should be visible (only for cost insights and usage tabs)
  const shouldShowTable =
    active === 'cost insights' || active === 'usage' || active === 'query run time';

  return (
    <>
      <RecentSection executionInfo={executionInfo} />
      {!isTestResource && !isSnapshotResource && (
        <DetailsSection heading="Jobs" withCard={false}>
          <Table
            isSortable={false}
            data={pendingData || []}
            columns={[
              {
                header: 'Job Name',
                accessorKey: 'jobName',
                id: 'name',
                cell: LinkCell,
              },
            ]}
            onRowClickLinkGenerator={
              getJobLink
                ? (row) => {
                    return getJobLink({
                      ...projectScope,
                      jobId: row.jobId,
                      projectId: projectId || 0,
                    });
                  }
                : undefined
            }
            isLoading={isInitialLoad}
            testId="model-execution-jobs-table"
          />
        </DetailsSection>
      )}
      <Card>
        <div data-testid="model-execution-performance-charts">
          <div className="space-y-6 px-4">
            <div className="flex flex-wrap">
              <div className="flex-1 whitespace-nowrap text-xl">
                {isTestResource
                  ? 'Test performance'
                  : isSnapshotResource
                    ? 'Snapshot performance'
                    : 'Model performance'}
              </div>
              <ChartDateDropdown
                onChange={setDateRange}
                oldestDate={oldestAvailableDate}
              />{' '}
            </div>

            {!isCostInsightsEnablementLoading &&
              featureFlags.meta5835EnableCostEstimationFrontend &&
              banner}
            {/** Show overview container when user has cost insights access, regardless of banner */}
            {!isCostInsightsEnablementLoading && canViewProjectCostData && (
              <OverviewContainer
                data={filteredCostData}
                isLoading={costInsightsLoading || isCostPreparing}
              />
            )}

            <div className="overflow-x-auto">
              <FloatingTabs testId="model-executions-chart-tabs">
                {availableTabs.map((t) => {
                  return (
                    <FloatingTabs.Tab
                      text={t.charAt(0).toUpperCase() + t.slice(1)}
                      id={t}
                      key={t}
                      isActive={active === t}
                      onClick={(tab: TabType) => {
                        setSelectedDayFilter(null);
                        setBuildTimeFilterDate(null);
                        setActive(tab);
                      }}
                      testId={`model-executions-chart-tabs-${t.replaceAll(' ', '-')}`}
                    />
                  );
                })}
              </FloatingTabs>
            </div>
            <>
              {active === 'cost insights' && canViewProjectCostData && (
                <div className="flex flex-col gap-4">
                  <CostChart
                    data={filteredCostData}
                    isLoading={costInsightsLoading}
                    isPreparing={isCostPreparing}
                    grouping={grouping}
                    setGrouping={setGrouping}
                    connectionSettingsUrl={connectionSettingsUrl}
                    statusBadge={statusBadge}
                  />
                </div>
              )}
              {active === 'build time' && (
                <>
                  <AverageModelExecutionChart
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    environmentId={environmentId || 0}
                    uniqueId={uniqueId}
                    setOldestDate={setOldestAvailableDate}
                    onItemClicked={(filter: JobFilter) => {
                      setBuildTimeFilterDate(filter.date);
                    }}
                    selectedDate={buildTimeFilterDate ?? undefined}
                  />
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span
                        className="text-sm text-fgDecorative"
                        data-testid="build-time-table-date-label"
                      >
                        {buildTimeFilterDate
                          ? `Showing runs for ${buildTimeFilterDate}`
                          : 'Showing all runs'}
                      </span>
                      {buildTimeFilterDate && (
                        <button
                          className="text-sm text-fgBrand hover:underline"
                          onClick={() => setBuildTimeFilterDate(null)}
                          data-testid="build-time-show-all-runs"
                        >
                          Show all runs
                        </button>
                      )}
                    </div>
                    <ExecutionRunTable
                      startDate={buildTimeFilterDate ?? dateRange.startDate}
                      endDate={buildTimeFilterDate ?? dateRange.endDate}
                      environmentId={environmentId || 0}
                      accountId={accountId || 0}
                      projectId={projectId || 0}
                      uniqueId={uniqueId}
                    />
                  </div>
                </>
              )}
              {active === 'build count' && (
                <>
                  <ModelExecutionStatusStackedBarChart
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    environmentId={environmentId || 0}
                    uniqueId={uniqueId}
                    setOldestDate={setOldestAvailableDate}
                    onItemClicked={setSelectedDayFilter}
                  />
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span
                        className="text-sm text-fgDecorative"
                        data-testid="build-count-table-date-label"
                      >
                        {selectedDayFilter
                          ? `Showing runs for ${selectedDayFilter.date}${selectedDayFilter.status ? ` (${selectedDayFilter.status})` : ''}`
                          : 'Showing all runs'}
                      </span>
                      {selectedDayFilter && (
                        <button
                          className="text-sm text-fgBrand hover:underline"
                          onClick={() => setSelectedDayFilter(null)}
                          data-testid="build-count-show-all-runs"
                        >
                          Show all runs
                        </button>
                      )}
                    </div>
                    <ExecutionRunTable
                      startDate={selectedDayFilter?.date ?? dateRange.startDate}
                      endDate={selectedDayFilter?.date ?? dateRange.endDate}
                      status={selectedDayFilter?.status}
                      environmentId={environmentId || 0}
                      accountId={accountId || 0}
                      projectId={projectId || 0}
                      uniqueId={uniqueId}
                    />
                  </div>
                </>
              )}
              {active === 'test results' && (
                <>
                  <ModelTestExecutionStatusStackedBarChart
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    environmentId={environmentId || 0}
                    uniqueId={uniqueId}
                    setOldestDate={setOldestAvailableDate}
                    onItemClicked={setSelectedDayFilter}
                  />
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span
                        className="text-sm text-fgDecorative"
                        data-testid="test-results-table-date-label"
                      >
                        {selectedDayFilter
                          ? `Showing runs for ${selectedDayFilter.date}${selectedDayFilter.status ? ` (${selectedDayFilter.status})` : ''}`
                          : 'Showing all runs'}
                      </span>
                      {selectedDayFilter && (
                        <button
                          className="text-sm text-fgBrand hover:underline"
                          onClick={() => setSelectedDayFilter(null)}
                          data-testid="test-results-show-all-runs"
                        >
                          Show all runs
                        </button>
                      )}
                    </div>
                    <ExecutionRunTable
                      startDate={selectedDayFilter?.date ?? dateRange.startDate}
                      endDate={selectedDayFilter?.date ?? dateRange.endDate}
                      status={selectedDayFilter?.status}
                      environmentId={environmentId || 0}
                      accountId={accountId || 0}
                      projectId={projectId || 0}
                      uniqueId={uniqueId}
                    />
                  </div>
                </>
              )}
              {active === 'consumption queries' && (
                <>
                  <ModelQueryUsageBarChart
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    environmentId={environmentId || 0}
                    uniqueId={uniqueId}
                    setOldestDate={setOldestAvailableDate}
                  />
                </>
              )}
              {active === 'usage' && canViewProjectNonCostData && (
                <div
                  className="flex flex-col gap-4"
                  data-testid="usage-chart-container"
                >
                  <UsageChart
                    data={usageChartData}
                    isLoading={costInsightsLoading}
                    isPreparing={isCostPreparing}
                    grouping={grouping}
                    setGrouping={setGrouping}
                    statusBadge={statusBadge}
                  />
                </div>
              )}
              {active === 'query run time' && canViewProjectNonCostData && (
                <div
                  className="flex flex-col gap-4"
                  data-testid="query-run-time-chart-container"
                >
                  <RunChart
                    data={filteredCostData}
                    isLoading={costInsightsLoading}
                    isPreparing={isCostPreparing}
                    grouping={grouping}
                    setGrouping={setGrouping}
                    statusBadge={statusBadge}
                  />
                </div>
              )}
              {/* Table for cost insights and usage tabs */}
              {shouldShowTable && canViewProjectCostData && (
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between">
                    <Button
                      text={`${showChartTable ? 'Hide' : 'Show'} table`}
                      onClick={() => setShowChartTable(!showChartTable)}
                      type="secondary"
                      ryecon={showChartTable ? RyeconCaretUp : RyeconCaretDown}
                      iconPlacement="right"
                      testId="cost-insights-table-toggle"
                    />
                    {showChartTable && (
                      <MenuButton
                        ryecon={RyeconDownload}
                        items={[{ label: 'CSV', value: 'csv' }]}
                        onClick={
                          active === 'cost insights'
                            ? handleCostInsightsDownload
                            : handleUsageDownload
                        }
                        text="Download"
                        isDisabled={filteredCostData.length === 0 || isCostPreparing}
                      />
                    )}
                  </div>
                  {showChartTable && (
                    <ChartTable
                      data={filteredCostData}
                      grouping={grouping}
                      isLoading={costInsightsLoading}
                      isPreparing={isCostPreparing}
                    />
                  )}
                </div>
              )}
            </>
          </div>
        </div>
      </Card>
    </>
  );
};
